import { useMemo } from "react";

import {
    type RVTask,
    CONFERENCE_PARTICIPANT_STATUS,
    ConferenceParticipantStatus,
    TASK_RESERVATION_STATUSES,
} from "@regal-voice/shared-types";
import { useSelector } from "react-redux";

import { TASK_STATUS_DISPLAY } from "Services/state/conferences/ConferenceSnapshotConstants";
import {
    selectConferenceSnapshotContactParticipantForTask,
    selectSnapshotTaskStatus,
    selectSnapshotTimeDisplay,
} from "Services/state/conferences/ConferenceSnapshotSelectors";
import { isPendingInboundConferenceTask, isPendingInboundTransferTask } from "Services/Task.service";

function isOneOf(status: string | undefined, statuses: ConferenceParticipantStatus[]) {
    if (!status) {
        return false;
    }
    return statuses.includes(status as ConferenceParticipantStatus);
}

type UseTaskStatusDisplay = (params: { task?: RVTask; useBarVariant?: boolean }) => {
    statusDisplay: string | null;
    timeDisplay: string | null;
    shouldShowLiveTime: boolean;
    shouldShowPendingTime: boolean;
} & (
    | { shouldShowLiveTime: true; timeDisplay: string }
    | { shouldShowPendingTime: true; timeDisplay: string }
    | { shouldShowLiveTime: false; shouldShowPendingTime: false }
);

export const useTaskStatusDisplay: UseTaskStatusDisplay = ({ task, useBarVariant }) => {
    const contactParticpant = useSelector(selectConferenceSnapshotContactParticipantForTask)(task);
    const isIncomingCall = isPendingInboundConferenceTask(task);
    const isPendingIncomingCallTransfer = isPendingInboundTransferTask(task);
    const callStatus = useSelector(selectSnapshotTaskStatus)(task);
    const { timeDisplay } = useSelector(selectSnapshotTimeDisplay)(task);

    const statusDisplay = useMemo(() => {
        if (useBarVariant) {
            if (callStatus === TASK_STATUS_DISPLAY.LIVE) {
                return null;
            }
        } else {
            const incomingCallRingingOrTransfer =
                isIncomingCall && (callStatus === TASK_STATUS_DISPLAY.RINGING || isPendingIncomingCallTransfer);

            const isPendingTaskWithoutContact = task?.status == TASK_RESERVATION_STATUSES.PENDING && !contactParticpant;

            if (incomingCallRingingOrTransfer || isPendingTaskWithoutContact) {
                return null;
            }
        }

        return callStatus;
    }, [callStatus, isIncomingCall, isPendingIncomingCallTransfer, useBarVariant, contactParticpant, task]);

    const shouldShowPendingTime = useMemo(() => {
        const hasContactOrIsPendingCallTransfer =
            contactParticpant?.status === CONFERENCE_PARTICIPANT_STATUS.ringing || isPendingIncomingCallTransfer;
        const taskIsPending = task?.status == TASK_RESERVATION_STATUSES.PENDING;

        if (useBarVariant) {
            if (taskIsPending) {
                if (hasContactOrIsPendingCallTransfer) {
                    return false;
                }
                return true;
            }
        } else {
            if (isIncomingCall) {
                if ((taskIsPending && !contactParticpant?.status) || hasContactOrIsPendingCallTransfer) {
                    return true;
                }
            } else {
                if (taskIsPending && !hasContactOrIsPendingCallTransfer) {
                    return true;
                }
            }
        }

        return false;
    }, [isIncomingCall, isPendingIncomingCallTransfer, useBarVariant, contactParticpant?.status, task?.status]);

    const shouldShowLiveTime = useMemo(() => {
        if (shouldShowPendingTime) {
            return false;
        }

        if (callStatus === TASK_STATUS_DISPLAY.WRAP_UP) {
            return true;
        }

        const statuses = [CONFERENCE_PARTICIPANT_STATUS.initiated, CONFERENCE_PARTICIPANT_STATUS.ringing];
        if (useBarVariant) {
            if (isOneOf(contactParticpant?.status, statuses) || isPendingIncomingCallTransfer) {
                return false;
            }
        } else if (!isIncomingCall && isOneOf(contactParticpant?.status, statuses)) {
            return false;
        }
        return true;
    }, [
        callStatus,
        isIncomingCall,
        isPendingIncomingCallTransfer,
        useBarVariant,
        contactParticpant?.status,
        shouldShowPendingTime,
    ]);

    return {
        statusDisplay,
        timeDisplay,
        shouldShowLiveTime,
        shouldShowPendingTime,
    };
};
